import { Select } from '@headlessui/react';
import { useElementSize } from '@mantine/hooks';
import sortBy from 'lodash.sortby';
import PropTypes from 'prop-types';
import { useState } from 'react';
import Chart from 'react-apexcharts';

const MS_PER_DAY = 1000 * 60 * 60 * 24;
const AGE_BUCKETS = ['0-7 days', '8-21 days', '22+ days'];
const AGE_COLORS = ['#22c55e', '#eab308', '#ef4444'];

const buildAgingConfig = (data) => {
	const now = Date.now();
	const counts = {};
	const categorySet = new Set();

	data.forEach((row) => {
		const category = row.service_category;
		if (!category) return;
		const released = new Date(row.released_on).getTime();
		const end = row.completed_on ? new Date(row.completed_on).getTime() : now;
		const ageDays = (end - released) / MS_PER_DAY;

		let bucket;
		if (ageDays <= 7) bucket = AGE_BUCKETS[0];
		else if (ageDays <= 21) bucket = AGE_BUCKETS[1];
		else bucket = AGE_BUCKETS[2];

		categorySet.add(category);
		counts[bucket] = counts[bucket] || {};
		counts[bucket][category] = (counts[bucket][category] || 0) + 1;
	});

	const totals = {};
	categorySet.forEach((c) => {
		totals[c] = AGE_BUCKETS.reduce(
			(sum, b) => sum + ((counts[b] && counts[b][c]) || 0),
			0,
		);
	});
	const categories = sortBy([...categorySet], (c) => totals[c]);

	const series = AGE_BUCKETS.map((bucket) => ({
		name: bucket,
		data: categories.map((c) => (counts[bucket] && counts[bucket][c]) || 0),
	}));

	return { categories, series };
};

const TopServiceCategories = ({ data = [], statuses = [] }) => {
	const { ref, width } = useElementSize();
	const [groupBy, setGroupBy] = useState('client_birth_year');

	if (statuses.length > 0) {
		const filteredData = data.filter((row) =>
			statuses.includes(row.request_status),
		);
		const { categories, series } = buildAgingConfig(filteredData);
		const height = Math.max(categories.length * 50, 120);
		return (
			<div ref={ref}>
				<div className="flex flex-row">
					<div className="self-center text-xl grow">
						Top Service Categories
					</div>
					<Select
						className="select select-sm select-bordered"
						value={groupBy}
						onChange={({ target }) => setGroupBy(target.value)}
					>
						<option value="client_birth_year">Year</option>
						<option value="client_zip">Zip Code</option>
					</Select>
				</div>
				{categories.length === 0 ? (
					<div className="mt-4 text-center text-gray-500">
						No Service Category data available
					</div>
				) : (
					<Chart
						type="bar"
						width={width}
						height={height}
						options={{
							chart: { stacked: true },
							colors: AGE_COLORS,
							plotOptions: { bar: { horizontal: true } },
							xaxis: {
								categories,
								labels: {
									formatter: function (value) {
										return Number(value).toFixed(0);
									},
								},
							},
							legend: { position: 'top' },
							tooltip: {
								y: {
									formatter: (v) =>
										`${v} referral${v === 1 ? '' : 's'}`,
								},
							},
						}}
						series={series}
					/>
				)}
			</div>
		);
	}

	const chartData = data.reduce((prev, row) => {
		if (!prev[row.service_category]) {
			prev[row.service_category] = 1;
		} else {
			prev[row.service_category]++;
		}
		return prev;
	}, {});
	const mappedData = sortBy(
		Object.entries(chartData).map(([category, count]) => ({
			category,
			count,
		})),
		['count'],
	).reverse();
	const categories = mappedData.map(({ category }) => category);
	const seriesData = mappedData.map(({ count }) => count);
	const height = Math.max(mappedData.length * 50, 120);
	let tickAmount = 5;
	if (Math.max(...seriesData) < 5) {
		tickAmount = categories.length;
	}
	return (
		<div ref={ref}>
			<div className="flex flex-row">
				<div className="self-center text-xl grow">Top Service Categories</div>
				<Select
					className="select select-sm select-bordered"
					value={groupBy}
					onChange={({ target }) => setGroupBy(target.value)}
				>
					<option value="client_birth_year">Year</option>
					<option value="client_zip">Zip Code</option>
				</Select>
			</div>
			{mappedData.length === 0 ? (
				<div className="mt-4 text-center text-gray-500">
					No Service Category data available
				</div>
			) : (
				<Chart
					type="bar"
					width={width}
					height={height}
					options={{
						chart: { stacked: true },
						colors: ['#3abddb'],
						xaxis: {
							tickAmount,
							categories,
							labels: {
								formatter: function (value) {
									return value.toFixed(0);
								},
							},
						},
						plotOptions: {
							bar: {
								horizontal: true,
							},
						},
					}}
					series={[{ name: 'Count', data: seriesData }]}
				/>
			)}
		</div>
	);
};

TopServiceCategories.propTypes = {
	data: PropTypes.array,
	statuses: PropTypes.arrayOf(PropTypes.string),
};

export default TopServiceCategories;
